import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchSettings, ShopInfo, ShopSettings, BranchSummary, BranchDetail } from '../data/fetchSettings';
import { applyTheme } from '../theme/applyTheme';
import { isPreviewMode, ADMIN_ORIGIN } from '../theme/preview';
import { isThemeId } from '../theme/themes';
import { resolveBranchCandidate } from '../branch/resolveBranch';

interface SettingsContextValue {
  info: ShopInfo;
  status: string;
  settingsLoaded: boolean;
  /** Active branches of a franchise brand ([] for single shops). */
  branches: BranchSummary[];
  /** The branch this visit is in, or null. */
  branch: BranchDetail | null;
}

export const SettingsContext = createContext<SettingsContextValue>({
  info: {},
  status: 'active',
  settingsLoaded: false,
  branches: [],
  branch: null,
});

/**
 * Point the browser-tab favicon at the shop's current (admin-set) logo so it
 * stays in sync with the runtime logo. The per-shop index.html ships a static
 * logo favicon as the default/SEO fallback; this overrides it once settings load.
 */
function applyFavicon(logoUrl?: string): void {
  if (!logoUrl) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = logoUrl;
}

// Info keys a branch never inherits from the brand (same list as get_settings.BRANCH_ONLY).
const BRANCH_ONLY_KEYS = [
  'addressText', 'googleMapLink', 'phoneNumber', 'phoneLink', 'wifiLink',
  'googleReviewLink', 'efoodLink', 'woltLink', 'boxLink',
] as const;

/**
 * Admin preview of a branch: brand keys follow the admin's live form, branch-only contact info
 * stays the branch's from the server (the HQ address must never show as a store's).
 * ponytail: branch overrides (heroUrl, heroVideoUrl, infoText) preview the admin's brand value.
 */
function previewInfo(adminInfo: ShopInfo, branchInfo: ShopInfo | null): ShopInfo {
  if (!branchInfo) return adminInfo;
  const merged: ShopInfo = { ...adminInfo };
  for (const key of BRANCH_ONLY_KEYS) {
    if (branchInfo[key]) merged[key] = branchInfo[key];
    else delete merged[key];
  }
  return merged;
}

interface PreviewMessage {
  themeId?: string;
  brandColor?: string;
  accentColor?: string;
  info?: ShopInfo;
}

export function SettingsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  // The first path segment may name a branch (https://<brand>.menulynx.com/<branch>). Read once,
  // before the router exists: the server's answer decides the router's basename.
  const [candidate] = useState(() => resolveBranchCandidate(window.location.pathname));
  const [info, setInfo] = useState<ShopInfo>({});
  const [status, setStatus] = useState<string>('active');
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  // With a candidate nothing may render until the server has said whether it is a branch, in
  // preview mode too: a router mounted without the basename would send /<branch> to the catch-all.
  const [branchResolved, setBranchResolved] = useState<boolean>(candidate === null);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [branch, setBranch] = useState<BranchDetail | null>(null);

  useEffect(() => {
    const applyBranches = (result: ShopSettings) => {
      setBranches(result.branches);
      setBranch(result.branch);
      // A brand with branches but no branch in the URL (an unknown/inactive branch, or an old
      // brand-level link like /categories): the brand root (the picker), never a blank page.
      // Done before the first render of the router, so it starts at "/".
      if (!result.branch && result.branches.length > 0 && window.location.pathname !== '/') {
        window.history.replaceState(null, '', `/${window.location.search}`);
      }
      setBranchResolved(true);
    };

    if (isPreviewMode()) {
      let adminInfo: ShopInfo | null = null;
      let branchInfo: ShopInfo | null = null;
      const handler = (event: MessageEvent) => {
        if (event.origin !== ADMIN_ORIGIN) return;
        const msg = (event.data || {}) as PreviewMessage;
        if (msg.themeId && isThemeId(msg.themeId)) {
          applyTheme(msg.themeId, msg.brandColor, msg.accentColor);
        }
        if (msg.info && typeof msg.info === 'object') {
          adminInfo = msg.info;
          setInfo(previewInfo(adminInfo, branchInfo));
        }
        // Mark settings as loaded on the first valid message from the admin origin
        setSettingsLoaded(true);
      };
      window.addEventListener('message', handler);
      // In preview mode, unblock rendering immediately — the admin pushes the first
      // message momentarily; without this the app stays on the loading gate forever
      // (App returns null until settingsLoaded), so the live-preview iframe is blank.
      setSettingsLoaded(true);
      // The status still comes from the server: skipping the fetch here meant anyone could
      // add ?preview=1 to a blocked shop's URL and get the full menu. Branch data too, so the
      // admin can preview /<branch>?preview=1; theme + info stay the admin's.
      fetchSettings(candidate).then((result) => {
        setStatus(result.status || 'active');
        if (result.branch) {
          branchInfo = result.info;
          if (adminInfo) setInfo(previewInfo(adminInfo, branchInfo));
        }
        applyBranches(result);
      });
      return () => window.removeEventListener('message', handler);
    }

    // Normal (non-preview) mode: fetch once on mount
    fetchSettings(candidate).then((result) => {
      applyTheme(result.themeId, result.brandColor, result.accentColor);
      setInfo(result.info);
      setStatus(result.status || 'active');
      applyBranches(result);
      setSettingsLoaded(true);
    });

    return undefined;
  }, [candidate]);

  // Keep the favicon in sync with the shop's runtime logo.
  useEffect(() => {
    applyFavicon(info.logoUrl);
  }, [info.logoUrl]);

  return (
    <SettingsContext.Provider
      value={{ info, status, settingsLoaded: settingsLoaded && branchResolved, branches, branch }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useShopInfo(): ShopInfo {
  return useContext(SettingsContext).info;
}

export function useShopStatus(): string {
  return useContext(SettingsContext).status;
}

export function useSettingsLoaded(): boolean {
  return useContext(SettingsContext).settingsLoaded;
}

export function useBranches(): BranchSummary[] {
  return useContext(SettingsContext).branches;
}

export function useBranch(): BranchDetail | null {
  return useContext(SettingsContext).branch;
}
